import React, { useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import { Link, Container } from 'pcln-design-system'
import {
  ContentfulRichText,
  ICallToActionType,
  IToutType,
  IMerchModule
} from '@pcln/contentful-components'
import { MerchModule } from '@pcln/marketing'
import analytics from '@/shared-utils/analytics'
import {
  ANALYTICS_CATEGORY_FLIGHTS,
  ANALYTICS_CATEGORY_HOTELS,
  ANALYTICS_CATEGORY_PACKAGES,
  ANALYTICS_CATEGORY_HOME
} from '@/constants/analytics'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import AppleStore from './graphics/AppleStoreSvg'
import PlayStore from './graphics/PlayStoreSvg'

const ANALYTICS_CATEGORIES = {
  '/flights': ANALYTICS_CATEGORY_FLIGHTS,
  '/hotels': ANALYTICS_CATEGORY_HOTELS,
  '/vacationpackages': ANALYTICS_CATEGORY_PACKAGES,
  '/': ANALYTICS_CATEGORY_HOME
} as const

type ANALYTICS_CATEGORY_TYPE =
  | typeof ANALYTICS_CATEGORY_FLIGHTS
  | typeof ANALYTICS_CATEGORY_HOTELS
  | typeof ANALYTICS_CATEGORY_PACKAGES
  | typeof ANALYTICS_CATEGORY_HOME

const SVGS: any = {
  AppleStore,
  PlayStore
}
type nextLandingToutType = IToutType & {
  backgroundColor?: string
  credCardModWrapDefaultDisplay?: boolean
}
function renderSVG(svgName: 'AppleStore' | 'PlayStore') {
  const SVG = SVGS[svgName] || React.Fragment
  return <SVG />
}

const MerchModuleWrapper = ({
  backgroundColor = 'background.light',
  callsToAction,
  primaryCallToAction,
  displayOption,
  body,
  credCardModWrapDefaultDisplay = false,
  image,
  googleAnalyticsTracking,
  contentTypeId,
  columnDisplayOptions,
  mediaDisplay,
  mediaPosition,
  mediaDisplayRatio,
  media,
  paragraph,
  textAlign,
  hideMediaOnMobile,
  overline,
  heading,
  ...props
}: nextLandingToutType & IMerchModule) => {
  const router = useRouter()
  const pathname = router?.pathname
  const googleAnalyticsCategory: ANALYTICS_CATEGORY_TYPE =
    ANALYTICS_CATEGORIES[pathname as keyof typeof ANALYTICS_CATEGORIES]

  const isToutModule =
    callsToAction !== undefined ||
    image !== undefined ||
    body !== undefined ||
    contentTypeId === 'tout'
  useEffect(() => {
    if (googleAnalyticsTracking?.action && googleAnalyticsTracking?.label) {
      analytics.fireEvent({
        category: googleAnalyticsTracking.category
          ? googleAnalyticsTracking.category
          : 'promotion',
        action: googleAnalyticsTracking.action,
        label: googleAnalyticsTracking.label
      })
    }
  }, [googleAnalyticsTracking])

  const moduleButtons: JSX.Element | undefined = callsToAction ? (
    <>
      {callsToAction.map((cta: ICallToActionType) => {
        const linkVariation = cta.svgName ? 'link' : 'fill'
        return (
          <Link
            mr={2}
            key={cta.ariaLabel}
            variation={linkVariation}
            href={cta.url}
            target={cta.openInNewTab ? '_blank' : undefined}
            rel={cta.openInNewTab ? 'noopener' : undefined}
            aria-label={cta.ariaLabel}
            onClick={() => {
              if (cta.googleAnalyticsTracking?.action) {
                analytics.fireEvent({
                  action: cta.googleAnalyticsTracking.action,
                  category:
                    cta.googleAnalyticsTracking.category ||
                    googleAnalyticsCategory,
                  label: cta.googleAnalyticsTracking.label
                })
              }
            }}
          >
            {cta.svgName
              ? renderSVG(cta.svgName as 'AppleStore' | 'PlayStore')
              : cta.label}
          </Link>
        )
      })}
    </>
  ) : (
    primaryCallToAction && (
      <>
        <Link
          key={primaryCallToAction.ariaLabel}
          variation="fill"
          href={primaryCallToAction.url}
          target={primaryCallToAction.openInNewTab ? '_blank' : undefined}
          rel={primaryCallToAction.openInNewTab ? 'noopener' : undefined}
          aria-label={primaryCallToAction.ariaLabel}
          onClick={() => {
            if (primaryCallToAction.analyticsTracking?.action) {
              analytics.fireEvent({
                action: primaryCallToAction.analyticsTracking.action,
                category: primaryCallToAction.analyticsTracking.category,
                label: primaryCallToAction.analyticsTracking.label
              })
            }
          }}
        >
          {primaryCallToAction.label}
        </Link>
      </>
    )
  )

  const { isMobile } = useContext(BootstrapDataContext)
  const isBoxCheck = props?.backgroundDisplay === 'box'
  const imageDisplayMobile = hideMediaOnMobile === false
  if (isToutModule) {
    return (
      <>
        {!credCardModWrapDefaultDisplay ? (
          <Container>
            <MerchModule
              {...props}
              isBox
              mediaDisplay={displayOption}
              imageUrl={image && `${image?.asset?.file?.url}?h=600&q=70`}
              imageAlt={image?.altText}
              callToAction={image?.callToAction}
              headingTextStyle="heading2"
              taglineTextStyle="overline"
              bodyLineHeight="25px"
              isMobile={isMobile}
              boxProps={{
                mx: isMobile ? 0 : 4,
                mt: isMobile ? '40px' : 0,
                mb: 4,
                p: 4
              }}
              headingMB="0px"
              backgroundColor={backgroundColor}
              body={
                <ContentfulRichText
                  color="text.dark"
                  textStyle="article"
                  fontSize={3}
                  body={body as any}
                />
              }
              actions={moduleButtons}
            />
          </Container>
        ) : (
          <Container px={4}>
            <MerchModule
              {...props}
              isBox
              mediaDisplay={displayOption}
              textAlign="left"
              imageUrl={image && `${image?.asset?.file?.url}?h=600&q=70`}
              imageAlt={image?.altText}
              callToAction={image?.callToAction}
              headingTextStyle="heading2"
              taglineTextStyle="overline"
              bodyLineHeight="25px"
              isMobile={isMobile}
              boxProps={{ py: 4, px: 4, mb: 4 }}
              headingMB="0px"
              backgroundColor="background.light"
              body={
                <ContentfulRichText
                  color="text.dark"
                  textStyle="article"
                  fontSize={3}
                  body={body as any}
                />
              }
              actions={moduleButtons}
            />
          </Container>
        )}
      </>
    )
  }
  return (
    <>
      <Container p={4}>
        <MerchModule
          isBox={isBoxCheck}
          columnVariation={['100', null, columnDisplayOptions]}
          tagline={overline}
          headline={heading}
          mediaDisplay={mediaDisplay}
          mediaPosition={mediaPosition}
          imageAspectRatio={mediaDisplayRatio}
          imageDisplayMobile={imageDisplayMobile}
          backgroundColor={backgroundColor}
          isMobile={isMobile}
          {...props}
          textAlign={textAlign}
          imageUrl={media?.file.url}
          imageAlt={media?.description}
          callToAction={primaryCallToAction}
          headingTextStyle="heading2"
          taglineTextStyle="overline"
          bodyLineHeight="25px"
          headingMB="0px"
          body={
            <ContentfulRichText
              color="text.dark"
              textStyle="article"
              fontSize={3}
              body={paragraph as any}
            />
          }
          actions={moduleButtons}
        />
      </Container>
    </>
  )
}

export default MerchModuleWrapper
